import React from 'react';
import PropTypes from 'prop-types';
import { Bert } from 'meteor/themeteorchef:bert';
import { saveAs } from 'file-saver';

import {
  Box,
  Button,
  Grid,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextareaAutosize,
  TextField,
  Typography
} from '@mui/material';
import { isEmpty } from 'lodash';

import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PlagiarismOutlinedIcon from '@mui/icons-material/PlagiarismOutlined';

import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

const NewFunctionISL = ({ ipsOfIsl, templateList }) => {
  const [openModal, setOpenModal] = React.useState(false);
  const [tablePage, setTablePage] = React.useState(0);
  const [templateSelect, setTemplateSelect] = React.useState({});
  const [text, setText] = React.useState('');
  const fileInputRef = React.useRef(null);
  const [importFile, setImportFile] = React.useState(null);
  const [dataFile, setDataFile] = React.useState(null);
  const [ipsSelected, setIpsSelected] = React.useState(ipsOfIsl.links[0]);
  const [dataType, setDataType] = React.useState('code');

  const rowsPerPage = 10;

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: '#2A3F90',
      color: theme.palette.common.white
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14
    }
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    },
    '&:last-child td, &:last-child th': {
      border: 0
    }
  }));

  const StyledTable = styled(Table)(() => ({
    border: `1px solid #2A3F90`
  }));

  const generateRandomId = length => {
    const valor = Array.from({ length }, () => Math.random().toString(36)[2]).join('');
    return valor;
  };

  const handleChangePage = (event, newPage) => {
    setTablePage(newPage);
  };

  const handleChangeSelector = event => {
    const selectedUrl = event.target.value;
    const selectedObject = ipsOfIsl.links.find(
      element => element.url === selectedUrl && element.type === 'ISL'
    );
    setIpsSelected(selectedObject);
  };

  const extractJsonSegments = textProp => {
    // Esta función debe extraer y retornar los segmentos JSON del texto
    // Aquí se usa un ejemplo simple que asume que los segmentos JSON están entre llaves {}
    const jsonSegments = [];
    const regex = /{[^}]*}/g;
    let match;
    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(textProp)) !== null) {
      jsonSegments.push(match[0]);
    }
    return jsonSegments;
  };

  const isValidJsonSegment = segment => {
    try {
      JSON.parse(segment);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleChangeText = event => {
    const jsonSegments = extractJsonSegments(event.target.value);
    const allValid = jsonSegments.every(segment => isValidJsonSegment(segment));
    console.log(allValid);
    setText(event.target.value);
  };

  const loadFileToBack = async file => {
    generateRandomId(10);
    let firstAnswer = {};

    if (!file) {
      Bert.alert('Selecciona un archivo primero', 'danger');
      setImportFile('');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('disc', 'data3');

    try {
      const response = await fetch('https://sd1.d3.ipsilum.com/upload', {
        method: 'POST',
        body: formData,
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      });

      firstAnswer = await response.json();
      setDataFile(firstAnswer.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDownload = async row => {
    try {
      const response = await fetch(row.url_template);
      const blob = await response.blob();
      saveAs(blob, row.name);
    } catch (error) {
      console.error('Error downloading the file:', error);
    }
  };

  const addPathToUrl = () => {
    let { url } = ipsSelected;
    const pathISL = 'MG/isl/exec_isl';
    const pathAirflow = 'MG/airflow/exec_airflow';
    if (!url.endsWith('/')) {
      url += '/';
    }
    if (ipsSelected.type === 'Airflow') {
      return url + pathAirflow;
    }
    if (ipsSelected.type === 'ISL') {
      return url + pathISL;
    }
    return '';
  };

  const loadTemplate = async () => {
    const bodyConfiguration = {
      disk: 'data3',
      get_template: false,
      upload_template: dataType === 'code' ? text : dataFile.path
    };
    const response = await fetch(addPathToUrl(), {
      method: 'POST',
      body: JSON.stringify(bodyConfiguration),
      headers: { 'Content-Type': 'application/json' },
      mode: 'cors',
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    });
    const respuesta = await response.json();
    if (respuesta.status === 'OK' && respuesta.data === 'DONE') {
      Bert.alert('Load Template success', 'success');
    } else {
      Bert.alert(respuesta.data, 'danger');
    }

    return [];
  };

  const body = (
    <Box backgroundColor="white" height="100%" width="100%">
      <Box display="flex" height="10%" width="100%" alignItems="center" mr={5}>
        <Grid container direction="row" justifyContent="space-around">
          <Grid item xs={11} sx={{ justifyContent: 'space-around', display: 'flex' }} />
          <Grid
            item
            xs={1}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
          >
            <IconButton
              variant="outlined"
              onClick={() => {
                setOpenModal(false);
                setTemplateSelect({});
              }}
              sx={{ marginRight: '16px', color: '#2A3F90' }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>
      <Box height="90%" width="100%" ml={3} mr={3} justifyContent="center">
        <Grid container>
          <Grid item xs={6} md={5}>
            <Box sx={{ mb: 2 }}>
              <TableContainer component={Paper}>
                <StyledTable aria-label="customized table" size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell style={{ minWidth: 'fit-content' }} />
                      <StyledTableCell align="left">Archivos del servidor</StyledTableCell>
                      <StyledTableCell style={{ minWidth: 'fit-content' }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templateList
                      .slice(tablePage * rowsPerPage, tablePage * rowsPerPage + rowsPerPage)
                      .map(row => (
                        <StyledTableRow key={row.name}>
                          {templateSelect.name === row.name ? (
                            <IconButton
                              size="small"
                              aria-label="centrar"
                              style={{ color: '#2A3F90' }}
                            >
                              <RadioButtonCheckedIcon />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              aria-label="centrar"
                              style={{ color: '#2A3F90' }}
                              onClick={() => {
                                setTemplateSelect(row);
                                setText(row.text_template);
                              }}
                            >
                              <RadioButtonUncheckedIcon />
                            </IconButton>
                          )}
                          <StyledTableCell component="th" scope="row">
                            {row.name}
                          </StyledTableCell>
                          <StyledTableCell component="th" scope="row">
                            <IconButton
                              size="small"
                              aria-label="download"
                              onClick={() => {
                                // window.open(row.url_template, '_blank');
                                handleDownload(row);
                              }}
                              sx={{
                                color: '#2A3F90'
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                  </TableBody>
                </StyledTable>
              </TableContainer>
              <Box sx={{ width: '100%' }}>
                <TablePagination
                  rowsPerPageOptions={[10]}
                  component="div"
                  count={templateList.length}
                  rowsPerPage={rowsPerPage}
                  page={tablePage}
                  onPageChange={handleChangePage}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={6} md={6}>
            {!isEmpty(templateSelect) && (
              <Box sx={{ m: 2 }} height="100%">
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  {templateSelect.name}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    mb: 2
                  }}
                >
                  <Button
                    variant={dataType === 'code' ? 'contained' : 'outlined'}
                    sx={{ width: '15%', fontSize: 11 }}
                    onClick={() => setDataType('code')}
                    disabled={dataType === 'code'}
                  >
                    Show code
                  </Button>
                  <Button
                    variant={dataType === 'upload' ? 'contained' : 'outlined'}
                    sx={{ width: '20%', fontSize: 11, marginLeft: '8px' }}
                    onClick={() => setDataType('upload')}
                    disabled={dataType === 'upload'}
                  >
                    Upload File
                  </Button>
                </Box>
                {dataType === 'upload' ? (
                  <TextField
                    fullWidth
                    label="Nombre del archivo"
                    size="small"
                    value={importFile ? importFile.name : ''}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            variant="contained"
                            aria-label="upload"
                            component="label"
                            style={{
                              color: '#2A3F90'
                            }}
                            onClick={() => {
                              fileInputRef.current.click();
                            }}
                          >
                            <PlagiarismOutlinedIcon />
                          </IconButton>
                          <input
                            ref={fileInputRef}
                            type="file"
                            style={{ opacity: 0, position: 'absolute', zIndex: -1 }}
                            onChange={event => {
                              setImportFile(event.target.files[0]);
                              loadFileToBack(event.target.files[0]);
                            }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      maxHeight: '50vh',
                      overflow: 'auto',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      mb: 2
                    }}
                  >
                    <TextareaAutosize
                      value={text}
                      onChange={handleChangeText}
                      minRows={20}
                      style={{
                        width: '100%',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        backgroundColor: '#272822',
                        color: '#f8f8f2',
                        border: 'none',
                        padding: '12px',
                        borderRadius: '4px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel id="demo-select-label">Server</InputLabel>
                    <Select
                      labelId="demo-select-label"
                      id="demo-select"
                      label="Server"
                      size="small"
                      value={ipsSelected.url || ''}
                      onChange={handleChangeSelector}
                    >
                      {ipsOfIsl.links
                        .filter(item => item.type === 'ISL')
                        .map(item => (
                          <MenuItem key={item.url} value={item.url}>
                            {item.url}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    color="success"
                    size="small"
                    onClick={() => loadTemplate()}
                  >
                    Load template
                  </Button>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  return (
    <>
      <IconButton
        variant="outlined"
        onClick={() => {
          setOpenModal(true);
        }}
        sx={{ color: 'white' }}
      >
        <NoteAddIcon />
      </IconButton>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        style={{
          position: 'absolute',
          bgcolor: 'white',
          top: '0%',
          left: '0%'
        }}
      >
        {body}
      </Modal>
    </>
  );
};

export default NewFunctionISL;

NewFunctionISL.defaultProps = {};
NewFunctionISL.propTypes = {
  templateList: PropTypes.array.isRequired,
  ipsOfIsl: PropTypes.object.isRequired
};
